import Button from '../../components/button/Button';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import { useClientTheme } from '../../context/ThemeContext';
import { APPLICATION_STATUS } from '../../utils/Constants';
import Modal from '../../components/modal/modal';
import TextArea from '../../components/textarea/TextArea';
import { updateApplication } from '../../services/ApplicationService';
import { useNavigate } from 'react-router-dom';
import { Toast, useToast } from '../../components/toast/toast';
import Spinner from '../../components/Spinner/spinner';
import { useEffect, useState } from 'react';
import BalanceTable from '../common/BalanceTable';
import { routes } from '../../routes/routePaths';
import { StandardizedBalanceData } from '../../types/interfaces';

interface VisualizerProps {
  riskData: StandardizedBalanceData;
  handleNextStep?: () => void;
  onPreviousClick?: () => void;
  cuit?: string | undefined;
  applicationId?: string | undefined;
  withCollapsables?: boolean;
  isPdfMode?: boolean;
}

const Visualizer: React.FC<VisualizerProps> = ({
  riskData,
  handleNextStep,
  onPreviousClick,
  cuit,
  applicationId,
  withCollapsables = false,
  isPdfMode = false,
}) => {
  const { clientTheme } = useClientTheme();
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const navigate = useNavigate();
  const [totals, setTotals] = useState<any>([]);

  useEffect(() => {
    if (riskData && riskData.values && riskData.totals) {
      // Usar los totales ya calculados de la estructura estandarizada
      const totals = {
        total_current: riskData.totals.total_current,
        total_non_current: riskData.totals.total_non_current,
        total_net_worth: riskData.totals.total_net_worth,
        total_net_worth_and_non_current: riskData.totals.total_net_worth_and_non_current,
        gross_result: riskData.totals.gross_result,
        operating_result: riskData.totals.operating_result,
        ordinary_result: riskData.totals.ordinary_result,
        result_before_taxes: {
          extraordinary_income: riskData.totals.result_before_taxes.extraordinary_income,
          extraordinary_expenses: riskData.totals.result_before_taxes.extraordinary_expenses,
          total: riskData.totals.result_before_taxes.total
        },
        taxes: riskData.totals.taxes,
      };
      setTotals(totals);
    }
  }, [riskData]);

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const handleApplication = (status: string) => {
    setIsSendingApplication(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.approved.label,
        'Aceptaste la documentación. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigate(routes.dashboard);
        }, 1000);
      }
      setIsSendingApplication(false);
    });
  };
  return (
    <Card>
      {!riskData ? (
        <Card>
          <P type='1' variant='danger' align='center'>
            No se pudieron cargar los datos de análisis de riesgo.
          </P>
        </Card>
      ) : !riskData.values || !riskData.totals ? (
        <Card>
          <P type='1' variant='warning' align='center'>
            Los datos de análisis de riesgo están incompletos. Verifica que todos los campos estén disponibles.
          </P>
        </Card>
      ) : !totals ? (
        <Card>
          <P type='1' variant='warning' align='center'>
            Los totales no se pudieron calcular correctamente.
          </P>
        </Card>
      ) : (
        <Card>
          <Toast id='application-toast' />
          <Modal
            isOpen={isRejectApplication}
            onClose={() => setIsRejectApplication(false)}
            title={'Declinar o solicitar cambios'}
            primaryText='Solicitar cambios'
            primaryVariant='primary'
            secondaryText='Declinar solicitud'
            secondaryVariant='secondary'
            onSecondaryAction={() => {
              setIsRejectApplication(false);
              handleApplication(APPLICATION_STATUS.rejected.label);
            }}
            onPrimaryAction={() => {
              setIsRejectApplication(false);
              handleApplication(APPLICATION_STATUS.pending.label);
            }}
            children={renderRejectModal()}
          />
          <BalanceTable
            riskData={riskData.values}
            totals={totals}
            withCollapsables={withCollapsables}
            isPdfMode={isPdfMode}
          />
          {!withCollapsables && (
            <Card>
              <Card
                border={true}
                variant={clientTheme ? 'primary' : 'success'}
                modifier='weak'
                spacing={'3'}
                display='flex'
                justifyContent='center'
                gap='16px'
                margin={{ top: '2' }}
              >
                <H type='4' withMargin={false} align='center'>
                  Resultado del ejercicio
                </H>
                <H
                  type='4'
                  withMargin={false}
                  weight='bolder'
                  variant={clientTheme ? 'primary' : 'success'}
                  align='center'
                >
                  $ {totals.taxes}
                </H>
              </Card>
              <Card
                display='flex'
                justifyContent='space-between'
                margin={{ top: '5' }}
              >
                <Card width='280px'>
                  <Button
                    id='checkRisk-button-reject'
                    size='lg'
                    variant='secondary'
                    onClick={() => setIsRejectApplication(true)}
                    fullWidth
                    disabled={isSendingApplication}
                  >
                    {isSendingApplication ? (
                      <Spinner size='sm' variant='primary' />
                    ) : (
                      'Declinar o solicitar cambios'
                    )}
                  </Button>
                </Card>
                <Card
                  display='flex'
                  justifyContent='flex-end'
                  gap='16px'
                  width='420px'
                >
                  <Button
                    id='client-button-back'
                    size='lg'
                    variant='secondary'
                    fullWidth
                    onClick={onPreviousClick}
                    disabled={isSendingApplication}
                  >
                    {isSendingApplication ? (
                      <Spinner size='sm' variant='primary' />
                    ) : (
                      'Volver'
                    )}
                  </Button>
                  <Button
                    id='client-button-save'
                    size='lg'
                    variant='primary'
                    fullWidth
                    onClick={handleNextStep}
                    disabled={isSendingApplication}
                  >
                    {isSendingApplication ? (
                      <Spinner size='sm' variant='primary' />
                    ) : (
                      'Continuar'
                    )}
                  </Button>
                </Card>
              </Card>
            </Card>
          )}
        </Card>
      )}
    </Card>
  );
};

export default Visualizer;
